<script>
import {defineComponent, onMounted, onBeforeUnmount, ref, computed} from 'vue'
import {Link} from "@inertiajs/vue3";
import IconLogo from "@js/components/Icons/IconLogo.vue";
import ModalSelect from "@js/components/Input/ModalSelect.vue";
import IconChevronDown from "@js/components/Icons/IconChevronDown.vue";
import IconDesigner from "@js/components/Icons/IconDesigner.vue";
import IconBuilder from "@js/components/Icons/IconBuilder.vue";
import IconArchitecture from "@js/components/Icons/IconArchitecture.vue";
import IconLogoSquare from "@js/components/Icons/IconLogoSquare.vue";

export default defineComponent({
    name: "B2BTopBarAdaptive",
    components: {
        IconLogoSquare,
        IconArchitecture, IconBuilder, IconDesigner, IconChevronDown, ModalSelect, IconLogo, Link},
    setup() {
        const isLight = ref(false);            // светлый фон под хедером?
        const glassRgb = ref('217 217 217');   // фон стекла (как раньше)
        const scrolled = ref(false);

        const textColor = computed(() => isLight.value ? '#101113' : '#ffffff');

        const readThemeInline = (el) => {
            if (!el) return null;
            const s = el.style?.getPropertyValue('--theme-rgb')?.trim();
            if (s) return s;
            const d = el.dataset?.themeRgb?.trim();
            return d || null;
        };

        const parseRgb = (val) => {
            if (!val) return null;
            const cleaned = val.replace(/[,/]+/g, ' ').replace(/\s+/g, ' ').trim();
            const parts = cleaned.split(' ').map(Number);
            if (parts.length === 3 && parts.every(v => Number.isFinite(v) && v >= 0 && v <= 255)) return parts;
            return null;
        };

        const getSolidBgColor = (el) => {
            // идём вверх до первого непрозрачного background-color
            let cur = el;
            while (cur && cur !== document.documentElement) {
                const cs = getComputedStyle(cur);
                const bgc = cs.backgroundColor;     // "rgba(r,g,b,a)" или "rgb(r,g,b)" или "transparent"
                const m = bgc.match(/rgba?\(([^)]+)\)/i);
                if (m) {
                    const nums = m[1].split(',').map(x => parseFloat(x.trim()));
                    const [r, g, b, a = 1] = nums;
                    if (a > 0) return [r, g, b, a];
                }
                cur = cur.parentElement;
            }
            return null;
        };

        const luma = (r, g, b) => 0.299 * r + 0.587 * g + 0.114 * b; // достаточно для принятия решения

        const updateFromUnderHeader = () => {
            const header = document.getElementById('b2b-glass-header');
            if (!header) return;
            const rect = header.getBoundingClientRect();
            const x = Math.round(window.innerWidth / 2);
            let y = Math.round(rect.bottom + 5);        // Точка сразу под хедером
            if (y >= window.innerHeight) y = window.innerHeight - 1;

            // кто под хедером
            let el = document.elementFromPoint(x, y);
            if (!el || el === header) {
                // пробуем точку чуть ниже
                el = document.elementFromPoint(x, Math.min(y + 8, window.innerHeight - 1));
            }
            if (!el) return;

            // 1) приоритетно берём явный --theme-rgb / data-theme-rgb
            let raw = null, cur = el;
            while (cur && cur !== document.documentElement && !raw) {
                raw = readThemeInline(cur);
                cur = cur.parentElement;
            }
            if (raw) {
                const p = parseRgb(raw);
                if (p) {
                    glassRgb.value = `${p[0]} ${p[1]} ${p[2]}`;
                    isLight.value = luma(p[0], p[1], p[2]) > 160; // порог можно подстроить (150–170)
                    return;
                }
            }

            // 2) иначе берём реальный background-color ближайшего непрозрачного предка
            const solid = getSolidBgColor(el);
            if (solid) {
                const [r, g, b] = solid;
                glassRgb.value = `${r} ${g} ${b}`;
                isLight.value = luma(r, g, b) > 160;
            }
            // 3) иначе не трогаем текущие значения — сработает дефолт
        };

        let ticking = false;
        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                scrolled.value = window.scrollY > 8;
                updateFromUnderHeader();
                ticking = false;
            });
        };
        const onResize = () => updateFromUnderHeader();

        onMounted(() => {
            updateFromUnderHeader();
            window.addEventListener('scroll', onScroll, {passive: true});
            window.addEventListener('resize', onResize, {passive: true});
        });
        onBeforeUnmount(() => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
        });

        const glassStyle = computed(() => ({
            '--glass-rgb': glassRgb.value,
            '--glass-alpha': scrolled.value ? 0.22 : 0.12,
            '--glass-stroke-alpha': scrolled.value ? 0.35 : 0.25,
            '--glass-blur': scrolled.value ? '10px' : '14px',
        }));

        return {isLight, glassStyle, textColor};
    }
})
</script>

<template>
    <header id="b2b-glass-header"
            class="glass-header container flex items-center w-full m-auto fixed left-0 right-0 top-5 rounded-full backdrop-blur-sm text-white z-10 h-16 transition border"
            :class="[isLight ? 'bg-black/30' : 'bg-[#d9d9d903]']">
        <div class="container flex items-center justify-between">
            <Link :href="route('home')" aria-label="Лого">
                <IconLogo class="h-[44px] max-w-[200px] object-contain"
                          :text_color="textColor"
                          icon_first_color="#ffcf33"
                          icon_second_color="#ffcf33"
                          bottom_text_color="#ffcf33"/>
            </Link>
            <ModalSelect :buttonClass="'bg-transparent font-normal'" widthClass="w-64">
                <template #button>
                    <div
                        class="flex items-center gap-1 group-hover:text-navHoverColor transition border-2 border-baseYellow rounded-lg px-5 py-2.5">
                        <div>
                            B2B Услуги
                        </div>
                        <IconChevronDown
                            class="w-4 h-4 text-gray-600 group-hover:text-navHoverColor transition"/>
                    </div>
                </template>
                <!--                <template #button>-->
                <!--                    <div-->
                <!--                        class="flex items-center gap-1 transition border-2 rounded-lg px-5 py-2.5"-->
                <!--                        :class="isLight ? 'border-gray-800 text-gray-900 hover:text-gray-700' : 'border-baseYellow text-white hover:text-[var(&#45;&#45;baseYellow)]'">-->
                <!--                        <div>B2B Услуги</div>-->
                <!--                        <IconChevronDown class="w-4 h-4" :class="isLight ? 'text-gray-700' : 'text-gray-200'"/>-->
                <!--                    </div>-->
                <!--                </template>-->

                <template #menu>
                    <ul class=" text-sm text-gray-700">
                        <Link :href="route('B2BForBuilders')">
                            <li class="block px-4 py-4 font-medium cursor-pointer md:hover:bg-gray-100">
                                <div
                                    class="flex items-center gap-2 text-sm font-bold leading-tight mt-0.5">
                                    Девелоперам и застройщикам
                                </div>
                            </li>
                        </Link>
                        <Link :href="route('B2BForJBI')">
                            <li class="block px-4 py-4 font-medium cursor-pointer md:hover:bg-gray-100">
                                <div
                                    class="flex items-center gap-2 text-sm font-bold leading-tight mt-0.5">
                                    Производителям ЖБИ и Бетонным заводам
                                </div>
                            </li>
                        </Link>
                        <Link :href="route('B2BForLSTC')">
                            <li class="block px-4 py-4 font-medium cursor-pointer md:hover:bg-gray-100">
                                <div
                                    class="flex items-center gap-2 text-sm font-bold leading-tight mt-0.5">
                                    Производителям ЛСТК
                                </div>
                            </li>
                        </Link>
                        <Link :href="route('B2BForInstallationOrganizations')">
                            <li class="block px-4 py-4 font-medium cursor-pointer md:hover:bg-gray-100">
                                <div
                                    class="flex items-center gap-2 text-sm font-bold leading-tight mt-0.5">
                                    Монтажным организациям
                                </div>
                            </li>
                        </Link>
                        <Link :href="route('B2BForAgro')">
                            <li class="block px-4 py-4 font-medium cursor-pointer md:hover:bg-gray-100">
                                <div
                                    class="flex items-center gap-2 text-sm font-bold leading-tight mt-0.5">
                                    Сельхоз предприятиям
                                </div>
                            </li>
                        </Link>
                        <Link :href="route('B2BForWorkshops')">
                            <li class="block px-4 py-4 font-medium cursor-pointer md:hover:bg-gray-100">
                                <div
                                    class="flex items-center gap-2 text-sm font-bold leading-tight mt-0.5">
                                    Производительным цехам
                                </div>
                            </li>
                        </Link>
                    </ul>
                </template>
            </ModalSelect>
        </div>
    </header>
</template>

<style scoped>
/*.glass-header {
    color: #fff;
    background-color: rgba(var(--glass-rgb, 217 217 217), var(--glass-alpha, .12));
    -webkit-backdrop-filter: saturate(1.15) blur(var(--glass-blur, 14px));
    backdrop-filter: saturate(1.15) blur(var(--glass-blur, 14px));
    border: 1px solid rgba(255, 255, 255, var(--glass-stroke-alpha, .25));
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, .08), 0 10px 30px rgba(0, 0, 0, .08);
    transition: background-color .25s ease, border-color .25s ease, color .2s ease, backdrop-filter .25s ease, box-shadow .25s ease;
}*/

/*.glass-header.is-light {
    color: #101113;
}

!* тёмный текст на светлом фоне *!
.glass-header.is-dark {
    color: #ffffff;
}*/

/* светлый текст на тёмном фоне */
</style>
